import React, { useState } from "react";
import { ChevronDown, ChevronRight, ExternalLink } from "lucide-react";

/**
 * Displays axe-core Sections A/B/C in an expandable format.
 *
 * Each rule card shows: rule ID, impact, WCAG refs, affected-element count,
 * axe explanation (help), and per-node selectors, HTML snippets, and axe's
 * failureSummary. A "Rule details" link opens the axe documentation URL.
 *
 * Intentionally minimal styling for Stage 1 — polish ships in Stage 4.
 */
const SECTION_META = {
  section_a: {
    label: "Section A · WCAG failures",
    tone: "border-red-400 bg-red-50",
    badge: "bg-red-700 text-white",
    countKey: "rules_failed",
    empty: "No WCAG failures detected in the tested scope.",
  },
  section_b: {
    label: "Section B · Best-practice findings",
    tone: "border-amber-400 bg-amber-50",
    badge: "bg-amber-700 text-white",
    countKey: "rules_failed",
    empty: "No best-practice findings.",
  },
  section_c: {
    label: "Section C · Manual review required",
    tone: "border-zinc-400 bg-zinc-50",
    badge: "bg-zinc-800 text-white",
    countKey: "rules_incomplete",
    empty: "axe reported no incomplete checks — but this does not replace manual review.",
  },
};

function Node({ node, idx }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="border border-zinc-300 mt-1 bg-white" data-testid={`axe-node-${idx}`}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full text-left px-2 py-1 flex items-center gap-1 hover:bg-zinc-50 text-xs font-mono break-all"
        aria-expanded={open}
      >
        {open ? <ChevronDown className="w-3 h-3 shrink-0" /> : <ChevronRight className="w-3 h-3 shrink-0" />}
        <span className="truncate">{node.selector || "(no selector)"}</span>
        {node.impact && (
          <span className="ml-auto text-[10px] uppercase tracking-widest text-zinc-600">
            {node.impact}
          </span>
        )}
      </button>
      {open && (
        <div className="px-3 py-2 bg-zinc-50 border-t border-zinc-200 text-xs space-y-2">
          {node.html && (
            <div>
              <div className="uppercase tracking-widest text-[10px] text-zinc-500 mb-1">HTML snippet</div>
              <pre className="bg-white border border-zinc-300 p-2 overflow-x-auto text-[11px] leading-snug whitespace-pre-wrap break-all">
                {node.html}
              </pre>
            </div>
          )}
          {node.failure_summary && (
            <div>
              <div className="uppercase tracking-widest text-[10px] text-zinc-500 mb-1">
                axe failureSummary
              </div>
              <pre className="bg-white border border-zinc-300 p-2 whitespace-pre-wrap text-[11px] leading-snug">
                {node.failure_summary}
              </pre>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function RuleCard({ rule, source }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="border border-zinc-950 mb-2 bg-white" data-testid={`axe-rule-${rule.rule_id}`}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full text-left px-3 py-2 flex items-center gap-2 hover:bg-zinc-50"
        aria-expanded={open}
      >
        {open ? <ChevronDown className="w-4 h-4 shrink-0" /> : <ChevronRight className="w-4 h-4 shrink-0" />}
        <span className="font-mono text-sm font-bold">{rule.rule_id}</span>
        <span className="text-[10px] px-1.5 py-0.5 border border-zinc-950 uppercase tracking-widest">
          {rule.impact || "n/a"}
        </span>
        <span className="text-xs text-zinc-700 truncate">{rule.description}</span>
        <span className="ml-auto text-xs font-mono font-bold">
          {rule.instance_count} element{rule.instance_count === 1 ? "" : "s"}
        </span>
      </button>
      {open && (
        <div className="border-t border-zinc-300 p-3 space-y-2 text-xs">
          <div className="flex items-start gap-4 flex-wrap">
            <div>
              <div className="uppercase tracking-widest text-[10px] text-zinc-500">
                WCAG refs
              </div>
              <div className="font-mono">
                {rule.wcag_refs?.length ? rule.wcag_refs.join(", ") : "—"}
              </div>
            </div>
            <div className="flex-1 min-w-[200px]">
              <div className="uppercase tracking-widest text-[10px] text-zinc-500">
                Explanation
              </div>
              <div>{rule.help || rule.description}</div>
            </div>
          </div>
          {rule.help_url && (
            <a
              href={rule.help_url}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1 text-xs underline"
              data-testid={`axe-rule-help-${rule.rule_id}`}
            >
              Rule details on Deque University <ExternalLink className="w-3 h-3" />
            </a>
          )}
          <div>
            <div className="uppercase tracking-widest text-[10px] text-zinc-500 mb-1">
              Affected elements ({rule.instance_count})
            </div>
            {(rule.nodes || []).map((n, i) => (
              <Node key={i} idx={i} node={n} />
            ))}
          </div>
          <div className="text-[10px] italic text-zinc-500">
            Source: {source}
          </div>
        </div>
      )}
    </div>
  );
}

function Section({ id, findings }) {
  const meta = SECTION_META[id];
  const count = findings?.length || 0;
  const totalInstances = (findings || []).reduce(
    (s, r) => s + (r.instance_count || 0),
    0,
  );
  return (
    <div className={`border ${meta.tone} p-3 mb-3`} data-testid={`axe-section-${id}`}>
      <div className="flex items-center gap-2 mb-2 flex-wrap">
        <span className={`font-display text-[10px] font-black uppercase tracking-widest px-2 py-0.5 ${meta.badge}`}>
          {meta.label}
        </span>
        <span className="font-mono text-[11px] text-zinc-700">
          {count} rule{count === 1 ? "" : "s"} · {totalInstances} instance{totalInstances === 1 ? "" : "s"}
        </span>
      </div>
      {count === 0 ? (
        <div className="text-xs italic text-zinc-600">{meta.empty}</div>
      ) : (
        findings.map((r) => (
          <RuleCard
            key={r.rule_id}
            rule={r}
            source={
              id === "section_c"
                ? "axe-core incomplete result — requires human confirmation"
                : "axe-core violation"
            }
          />
        ))
      )}
    </div>
  );
}

export default function AxeFindingsPanel({ sections }) {
  const a = sections?.section_a || [];
  const b = sections?.section_b || [];
  const c = sections?.section_c || [];
  return (
    <div className="mb-4" data-testid="axe-findings-panel">
      <div className="font-display text-sm font-bold uppercase tracking-wider mb-2">
        axe-core evidence
      </div>
      <Section id="section_a" findings={a} />
      <Section id="section_b" findings={b} />
      <Section id="section_c" findings={c} />
    </div>
  );
}
